/**
 * W124.4 · 2026-05-26 · route-titles.ts
 *
 * Resolves a human-readable title from a pathname. The W124 smoke surfaced
 * that the global header was rendering "Obra" on EVERY `/projects/:id/*`
 * route — which clashed with the fact that "Obra" is also the name of one
 * of the 6 PM mesas (`/projects/:id/obra`). Confusing.
 *
 * This helper produces the title that the global TopBar (AppShell) shows
 * based on the current pathname. It is NOT the ProjectHero title
 * (W124.2 owns that), and it is NOT a per-mesa header (W124.5 owns those).
 *
 * Source of truth for labels:
 *   - PM mesas + Configurações → `PM_NAV` (already i18n-resolved labels)
 *   - Admin pages              → `ADMIN_NAV`
 *   - Special top-level paths  → hardcoded PT-BR strings (loaded directly
 *     by the user; not part of the PM_NAV registry).
 *
 * Why hardcoded PT-BR for specials (not `useT()`):
 *   - The function is pure (no React context). Callers that need i18n
 *     swap in their own resolver (e.g. AppShell can map via the t-tree).
 *   - In the current product, all six special routes (/projects,
 *     /projects/novo, /maintenance, /playground, /post-login, /login)
 *     ship with PT-BR labels because the global header is PT-BR by
 *     default for the PM persona.
 *
 * Public surface: `getTitleForRoute(pathname, projectId?)`.
 */

import { PM_NAV, ADMIN_NAV } from './nav-registry';

/**
 * Labels for PM mesas keyed by mesa slug (the URL segment after the
 * project id). Built once from `PM_NAV` so the source of truth stays
 * in nav-registry.
 *
 * The labels in PM_NAV are i18n keys (e.g. `nav.hoje`), but for the
 * route-title use case we only need the PT-BR resolved value — and the
 * resolved value happens to match the URL segment for 5 of 6 mesas
 * (hoje, plano, compras, obra, dinheiro, entrega). The `config` entry
 * needs its label spelled out ("Configurações") because the URL is
 * `/settings` and not `/configuracoes`.
 */
import { getLocale } from '@/i18n';

function isEn() { return getLocale() === 'en'; }

const PM_MESA_LABELS_PTBR: Record<string, string> = {
  hoje: 'Hoje',
  plano: 'Plano',
  compras: 'Compras',
  obra: 'Obra',
  dinheiro: 'Dinheiro',
  entrega: 'Entrega',
};

const PM_MESA_LABELS_EN: Record<string, string> = {
  hoje: 'Today',
  plano: 'Plan',
  compras: 'Purchases',
  obra: 'Site',
  dinheiro: 'Finance',
  entrega: 'Handover',
};

function PM_MESA_LABELS(): Record<string, string> {
  return isEn() ? PM_MESA_LABELS_EN : PM_MESA_LABELS_PTBR;
}

/**
 * Labels for the admin section. Matches the order/keys of ADMIN_NAV.
 * Keyed by the FIRST admin segment after `/admin/`. Multi-segment
 * admin routes (e.g. `/admin/labs/generative`) match by the first
 * segment after `/admin/`.
 */
const ADMIN_LABELS_PTBR: Record<string, string> = {
  'command-control': 'Comando',
  comando: 'Comando',
  saude: 'Saúde',
  brain: 'Brain',
  autonomia: 'Autonomia',
  deploys: 'Deploys',
  usuarios: 'Usuários',
  backups: 'Cópias de segurança',
  'audit-log': 'Histórico de auditoria',
  auditoria: 'Histórico de auditoria',
  'feature-flags': 'Bandeiras',
  labs: 'Labs',
  'meta-templates': 'E-mail (CF)',
  'system-tasks': 'Tarefas do sistema',
  insumos: 'SINAPI · Insumos',
  observability: 'Observability',
};

const ADMIN_LABELS_EN: Record<string, string> = {
  'command-control': 'Command',
  comando: 'Command',
  saude: 'Health',
  brain: 'Brain',
  autonomia: 'Autonomy',
  deploys: 'Deploys',
  usuarios: 'Users',
  backups: 'Backups',
  'audit-log': 'Audit history',
  auditoria: 'Audit history',
  'feature-flags': 'Feature flags',
  labs: 'Labs',
  'meta-templates': 'Email (CF)',
  'system-tasks': 'System tasks',
  insumos: 'SINAPI · Inputs',
  observability: 'Observability',
};

function ADMIN_LABELS(): Record<string, string> {
  return isEn() ? ADMIN_LABELS_EN : ADMIN_LABELS_PTBR;
}

/**
 * Resolves the title shown in the AppShell global header for the given
 * pathname.
 *
 *   /projects/:id/hoje    → "Hoje"
 *   /projects/:id/plano   → "Plano"
 *   /projects/:id/compras → "Compras"
 *   /projects/:id/obra    → "Obra"
 *   /projects/:id/dinheiro → "Dinheiro"
 *   /projects/:id/entrega  → "Entrega"
 *   /projects/:id (sem sub) → "Obra" (fallback project shell)
 *   /projects              → "Obras" (plural — lista)
 *   /projects/novo         → "Nova obra"
 *   /settings              → "Configurações"
 *   /config                → "Configurações"
 *   /hoje                  → "Hoje" (cross-obra hub)
 *   /campo                 → "Campo"
 *   /maintenance           → "Manutenção"
 *   /playground            → "Playground"
 *   /post-login            → "Bem-vindo"
 *   /login                 → "Login"
 *   /admin                 → "Admin"
 *   /admin/comando         → "Comando"
 *   /admin/<X>             → ADMIN_NAV match by first segment
 *   default                → "Builder"
 *
 * `projectId` is accepted for forward-compat (W124.5 may want to fold
 * project name into the title) but currently ignored — the route alone
 * is enough to derive the segment label.
 */
export function getTitleForRoute(
  pathname: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  projectId?: string,
): string {
  // Normalize: drop trailing slash, drop query/hash if accidentally
  // passed in. We only look at the path portion.
  let p = pathname || '/';
  const hashIdx = p.indexOf('#');
  if (hashIdx >= 0) p = p.slice(0, hashIdx);
  const qIdx = p.indexOf('?');
  if (qIdx >= 0) p = p.slice(0, qIdx);
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);

  // Specials — exact-match top-level routes.
  if (p === '/' || p === '') return 'Builder';
  if (p === '/login') return 'Login';
  if (p === '/post-login') return isEn() ? 'Welcome' : 'Bem-vindo';
  if (p === '/maintenance') return isEn() ? 'Maintenance' : 'Manutenção';
  if (p === '/playground') return 'Playground';
  if (p === '/projects') return isEn() ? 'Projects' : 'Obras';
  if (p === '/projects/novo') return isEn() ? 'New project' : 'Nova obra';
  if (p === '/settings' || p === '/config') return isEn() ? 'Settings' : 'Configurações';
  if (p === '/hoje') return isEn() ? 'Today' : 'Hoje';
  if (p === '/campo') return isEn() ? 'Field' : 'Campo';
  if (p === '/inbox') return 'Inbox';
  if (p === '/atividade') return isEn() ? 'Activity' : 'Atividade';
  if (p === '/carteira') return isEn() ? 'Portfolio' : 'Carteira';
  if (p === '/relatorios') return isEn() ? 'Reports' : 'Relatórios';
  if (p === '/fornecedores') return isEn() ? 'Suppliers' : 'Fornecedores';
  if (p === '/qualidade') return isEn() ? 'Quality' : 'Qualidade';
  if (p === '/alertas') return isEn() ? 'Alerts' : 'Alertas';
  if (p === '/anomalias') return isEn() ? 'Anomalies' : 'Anomalias';
  if (p === '/notificacoes') return isEn() ? 'Notifications' : 'Notificações';
  if (p === '/aprovacoes') return isEn() ? 'Approvals' : 'Aprovações';
  if (p === '/ajuda') return isEn() ? 'Help center' : 'Central de ajuda';
  if (p === '/owner') return isEn() ? 'My projects' : 'Minhas obras';
  if (p === '/broker') return isEn() ? 'Auctions' : 'Leilões';

  // /projects/:id[/<mesa>[/...]] — PM mesa lookup.
  const projMatch = p.match(/^\/projects\/([^/]+)(?:\/([^/]+))?(?:\/.*)?$/);
  if (projMatch) {
    const mesa = projMatch[2];
    const mesaLabels = PM_MESA_LABELS();
    if (mesa && mesaLabels[mesa]) {
      return mesaLabels[mesa];
    }
    // Settings sub-routes under project shell (rare; explicit map).
    if (mesa === 'settings' || mesa === 'config') return isEn() ? 'Settings' : 'Configurações';
    // Fall-through: project home (no mesa segment).
    return isEn() ? 'Site' : 'Obra';
  }

  // /admin[/<segment>[/...]] — admin section lookup.
  if (p === '/admin') return 'Admin';
  const adminMatch = p.match(/^\/admin\/([^/]+)(?:\/.*)?$/);
  if (adminMatch) {
    const seg = adminMatch[1] ?? '';
    const adminLabels = ADMIN_LABELS();
    if (seg && adminLabels[seg]) return adminLabels[seg]!;
    // Try resolving via ADMIN_NAV entry whose `to()` ends with the seg.
    const entry = ADMIN_NAV.find((g) => {
      const dest = g.to();
      const parts = dest.split('/').filter(Boolean);
      return parts[1] === seg; // [0]=admin, [1]=<seg>
    });
    if (entry && seg) {
      // labelKey resolution requires React/i18n context — for now we
      // capitalize the URL segment as a best-effort label. AppShell can
      // later swap this for a t-tree lookup if needed.
      return seg.charAt(0).toUpperCase() + seg.slice(1);
    }
    return seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : 'Admin';
  }

  // PM_NAV fallback by trailing segment (helps catch /projects/<id>/<mesa>
  // synonyms that we may add later without updating PM_MESA_LABELS).
  const mesaLabels = PM_MESA_LABELS();
  const lastSeg = p.split('/').filter(Boolean).pop();
  if (lastSeg && mesaLabels[lastSeg]) {
    return mesaLabels[lastSeg]!;
  }
  if (lastSeg) {
    const navHit = PM_NAV.find((g) => g.id === lastSeg);
    if (navHit && mesaLabels[navHit.id]) return mesaLabels[navHit.id]!;
  }

  return 'Builder';
}
